import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, ArrowRight, Instagram, Youtube, Facebook } from "lucide-react";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const { t } = useLang();

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error(t("contact.formError"));
            return;
        }
        setSubmitting(true);
        setTimeout(() => {
            toast.success(t("contact.formSuccess"));
            setForm({ name: "", email: "", phone: "", message: "" });
            setSubmitting(false);
        }, 800);
    };

    const input = "w-full bg-transparent border-b border-border py-4 text-lg font-serif placeholder:text-mist/70 focus:border-forest focus:outline-none transition-colors";

    return (
        <div data-testid="contact-page" className="bg-paper">
            <section className="pt-40 md:pt-48 pb-16 container-editorial">
                <SectionLabel label={t("contact.label")} number="D" className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight md:col-span-8 whitespace-pre-line">
                        {t("contact.heading")}
                    </h1>
                    <p className="md:col-span-4 text-ink/70 text-lg leading-relaxed">
                        {t("contact.intro")}
                    </p>
                </div>
            </section>

            <section className="pb-32 container-editorial">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-5 space-y-10">
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.phone")}</div>
                            <div className="flex items-center gap-3">
                                <Phone size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="tel:+919151515975" className="font-serif text-2xl kbd-num link-underline" data-testid="contact-phone">+91 91515 15975</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.whatsapp")}</div>
                            <div className="flex items-center gap-3">
                                <MessageCircle size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="https://wa.me/919151515975" target="_blank" rel="noopener noreferrer" className="font-serif text-2xl kbd-num link-underline" data-testid="contact-whatsapp">+91 91515 15975</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.email")}</div>
                            <div className="flex items-center gap-3">
                                <Mail size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="mailto:info@omkardairyfarm.com" className="font-serif text-2xl link-underline" data-testid="contact-email">info@omkardairyfarm.com</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.instagram")}</div>
                            <div className="flex items-center gap-3">
                                <Instagram size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="https://www.instagram.com/omkardiaryfarm" target="_blank" rel="noopener noreferrer" className="font-serif text-2xl link-underline" data-testid="contact-instagram">@omkardiaryfarm</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.youtube")}</div>
                            <div className="flex items-center gap-3">
                                <Youtube size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="https://www.youtube.com/results?search_query=omkardiaryfarm" target="_blank" rel="noopener noreferrer" className="font-serif text-2xl link-underline" data-testid="contact-youtube">@omkardiaryfarm</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.facebook")}</div>
                            <div className="flex items-center gap-3">
                                <Facebook size={18} strokeWidth={1.5} className="text-forest" />
                                <a href="https://www.facebook.com/share/1CmHcsPUWU/" target="_blank" rel="noopener noreferrer" className="font-serif text-2xl link-underline" data-testid="contact-facebook">Omkar Dairy Farm</a>
                            </div>
                        </div>
                        <div>
                            <div className="overlabel text-earth mb-3">{t("contact.address")}</div>
                            <div className="flex items-start gap-3">
                                <MapPin size={18} strokeWidth={1.5} className="text-forest mt-1" />
                                <a
                                    href="https://www.google.com/maps/place/12%C2%B047'06.5%22N+76%C2%B050'22.2%22E/@12.785131,76.839485,17z"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-serif text-2xl leading-snug link-underline whitespace-pre-line"
                                    data-testid="contact-address"
                                >
                                    {t("contact.addressBody")}
                                </a>
                            </div>
                        </div>
                    </div>

                    <Reveal className="md:col-span-7">
                        <div className="bg-white border border-border rounded-[12px] p-8 md:p-12 shadow-soft">
                            <div className="overlabel text-earth mb-3">{t("contact.enquiryLabel")}</div>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mb-10">
                                {t("contact.enquiryTitle")}
                            </h2>
                            <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                                <input type="text" placeholder={t("contact.fieldName")} className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <input type="email" placeholder={t("contact.fieldEmail")} className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" />
                                    <input type="tel" placeholder={t("contact.fieldPhone")} className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-input-phone" />
                                </div>
                                <textarea rows={5} placeholder={t("contact.fieldMessage")} className={input + " resize-none"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" />
                                <button type="submit" disabled={submitting} data-testid="contact-submit" className="group inline-flex items-center gap-3 bg-forest text-paper px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-gold hover:text-ink transition-colors duration-500 disabled:opacity-60">
                                    {submitting ? t("common.buttons.sending") : t("common.buttons.sendEnquiry")}
                                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </form>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="pb-32 container-editorial" data-testid="contact-map">
                <div className="relative aspect-[16/8] rounded-[12px] overflow-hidden bg-bone border border-border shadow-soft">
                    <iframe title="Omkar Dairy Farm location" src="https://maps.google.com/maps?q=12.785131,76.839485&hl=en&z=16&output=embed" className="absolute inset-0 w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                </div>
                <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4">
                    <div>
                        <div className="overlabel text-earth mb-2">{t("contact.directions")}</div>
                        <div className="font-serif text-xl">{t("contact.addressShort")}</div>
                        <div className="text-sm text-mist mt-1 kbd-num">12°47&apos;06.5&quot;N&nbsp;&nbsp;76°50&apos;22.2&quot;E</div>
                    </div>
                    <a href="https://www.google.com/maps/place/12%C2%B047'06.5%22N+76%C2%B050'22.2%22E/@12.785131,76.839485,17z" target="_blank" rel="noopener noreferrer" data-testid="contact-open-in-maps" className="inline-flex items-center gap-2 text-sm text-forest border border-forest px-5 py-2 rounded-full hover:bg-forest hover:text-paper transition-colors duration-300">
                        {t("common.buttons.openMaps")}
                    </a>
                </div>
            </section>
        </div>
    );
}
